import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchMailFoldersQueued,
  fetchMailFoldersSequential,
} from "@/lib/api/folderFetchCoordinator";
import { MAIL_LIVE_SYNC_MS } from "@/lib/constants/inbox";
import {
  clearFoldersSessionCache,
  readFoldersSessionCache,
  writeFoldersSessionCache,
} from "@/lib/cache/foldersSessionCache";
import { normalizeMailFolderLabels } from "@/lib/folders/folderLabels";
import { syncFolderHeaders } from "@/lib/mail/folderWarmup";
import { enqueueStandardFolderWarm } from "@/lib/mail/folderHeadersPrefetch";
import { folderUnreadAfterDeleteHold } from "@/lib/mail/mailMutationHold";
import { syncInboxHeadersIfBadgeAhead } from "@/lib/mail/syncInboxIfBadgeAhead";
import { ensureAureliaFolderOverrides } from "@/lib/mail/sentFolderSetup";
import type { MailFolder } from "@/lib/types/mail";

const FALLBACK_FOLDERS: MailFolder[] = [
  { name: "INBOX", label: "Boîte de réception", depth: 0, unread: 0 },
];

function seedFolders(accountId: number): MailFolder[] {
  const session = readFoldersSessionCache(accountId);
  if (session?.length) return normalizeMailFolderLabels(session);
  return FALLBACK_FOLDERS;
}

/**
 * Dossiers pour chaque compte ouvert dans l’accordéon (cache session + compte actif live).
 * Fetch IMAP séquentiel — jamais Promise.all (MAIL_PIPELINE § piège connexions).
 */
export function useExpandedAccountsFolders(
  expandedAccountIds: ReadonlySet<number>,
  activeAccountId: number | undefined,
  activeFolders: MailFolder[],
  activeFoldersLoading: boolean,
  adjustActiveFolderUnread?: (folder: string, delta: number) => void,
  options?: { pausedAccountIds?: ReadonlySet<number> }
) {
  const pausedAccountIds = options?.pausedAccountIds;
  const isPaused = useCallback(
    (accountId: number) => pausedAccountIds?.has(accountId) === true,
    [pausedAccountIds]
  );
  const [foldersByAccount, setFoldersByAccount] = useState<Map<number, MailFolder[]>>(
    () => new Map()
  );
  const [loadingIds, setLoadingIds] = useState<Set<number>>(() => new Set());
  const inflightRef = useRef(new Set<number>());

  useEffect(() => {
    if (activeAccountId == null) return;
    setFoldersByAccount((prev) => {
      const next = new Map(prev);
      next.set(activeAccountId, activeFolders);
      return next;
    });
  }, [activeAccountId, activeFolders]);

  useEffect(() => {
    if (activeFoldersLoading) return;

    const expanded = [...expandedAccountIds].filter(
      (id) => id !== activeAccountId && !isPaused(id)
    );
    if (expanded.length === 0) return;

    for (const accountId of expanded) {
      setFoldersByAccount((prev) => {
        if (prev.has(accountId) && (prev.get(accountId)?.length ?? 0) > 1) return prev;
        const seeded = seedFolders(accountId);
        const next = new Map(prev);
        next.set(accountId, seeded);
        return next;
      });
    }

    let cancelled = false;

    void (async () => {
      for (const accountId of expanded) {
        if (cancelled) break;
        if (inflightRef.current.has(accountId)) continue;
        const cached = readFoldersSessionCache(accountId);
        if (cached && cached.length > 1) continue;

        inflightRef.current.add(accountId);
        setLoadingIds((prev) => new Set(prev).add(accountId));

        const res = await fetchMailFoldersQueued({ accountId, refresh: false });

        inflightRef.current.delete(accountId);
        setLoadingIds((prev) => {
          const next = new Set(prev);
          next.delete(accountId);
          return next;
        });

        if (cancelled || !res.ok || !expandedAccountIds.has(accountId)) continue;

        const list = normalizeMailFolderLabels(
          res.data.folders?.length ? res.data.folders : FALLBACK_FOLDERS
        );
        writeFoldersSessionCache(accountId, list);
        ensureAureliaFolderOverrides(accountId, list);
        enqueueStandardFolderWarm(accountId, list);
        setFoldersByAccount((prev) => {
          const next = new Map(prev);
          next.set(accountId, list);
          return next;
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [activeAccountId, activeFoldersLoading, expandedAccountIds, isPaused]);

  const getFolders = useCallback(
    (accountId: number): MailFolder[] => {
      if (accountId === activeAccountId) return activeFolders;
      return foldersByAccount.get(accountId) ?? seedFolders(accountId);
    },
    [activeAccountId, activeFolders, foldersByAccount]
  );

  const isFoldersLoading = useCallback(
    (accountId: number): boolean => {
      if (accountId === activeAccountId) return activeFoldersLoading;
      return loadingIds.has(accountId) && (foldersByAccount.get(accountId)?.length ?? 0) <= 1;
    },
    [activeAccountId, activeFoldersLoading, foldersByAccount, loadingIds]
  );

  const adjustAccountFolderUnread = useCallback(
    (accountId: number, folderName: string, delta: number) => {
      if (delta === 0) return;

      if (accountId === activeAccountId && adjustActiveFolderUnread) {
        adjustActiveFolderUnread(folderName, delta);
        return;
      }

      setFoldersByAccount((prev) => {
        const current = prev.get(accountId) ?? seedFolders(accountId);
        const nextList = current.map((f) =>
          f.name === folderName
            ? { ...f, unread: Math.max(0, (f.unread ?? 0) + delta) }
            : f
        );
        writeFoldersSessionCache(accountId, nextList);
        const next = new Map(prev);
        next.set(accountId, nextList);
        return next;
      });
    },
    [activeAccountId, adjustActiveFolderUnread]
  );

  const refreshExpandedBadges = useCallback(async () => {
    const ids = [...expandedAccountIds].filter(
      (id) => id !== activeAccountId && !isPaused(id)
    );
    if (ids.length === 0) return;

    const results = await fetchMailFoldersSequential(
      ids.map((accountId) => ({ accountId, badges: true }))
    );

    const reconciledByAccount = new Map<number, MailFolder[]>();
    setFoldersByAccount((prev) => {
      const next = new Map(prev);
      ids.forEach((accountId, index) => {
        const res = results[index];
        if (!res.ok) return;
        const list = normalizeMailFolderLabels(
          res.data.folders?.length ? res.data.folders : FALLBACK_FOLDERS
        );
        const previous = prev.get(accountId) ?? [];
        const reconciled = list.map((folder) => {
          const local = previous.find((item) => item.name === folder.name);
          const unread = folderUnreadAfterDeleteHold(
            accountId,
            folder.name,
            folder.unread ?? 0,
            local?.unread
          );
          return unread === (folder.unread ?? 0) ? folder : { ...folder, unread };
        });
        writeFoldersSessionCache(accountId, reconciled);
        reconciledByAccount.set(accountId, reconciled);
        next.set(accountId, reconciled);
      });
      return next;
    });

    for (let index = 0; index < ids.length; index += 1) {
      const accountId = ids[index];
      const res = results[index];
      if (!res?.ok) continue;
      const list = reconciledByAccount.get(accountId);
      if (!list) continue;
      await syncInboxHeadersIfBadgeAhead(accountId, list);
    }
  }, [activeAccountId, expandedAccountIds, isPaused]);

  useEffect(() => {
    if (expandedAccountIds.size === 0 || activeFoldersLoading) return;

    const tick = () => {
      if (document.visibilityState !== "visible") return;
      void refreshExpandedBadges();
    };

    const deferId = window.setTimeout(() => {
      void refreshExpandedBadges();
    }, MAIL_LIVE_SYNC_MS);

    const intervalId = window.setInterval(tick, MAIL_LIVE_SYNC_MS);
    return () => {
      window.clearTimeout(deferId);
      window.clearInterval(intervalId);
    };
  }, [activeAccountId, activeFoldersLoading, expandedAccountIds, refreshExpandedBadges]);

  const refreshAllAccounts = useCallback(
    async (accountIds: number[], force = false) => {
      const targets = accountIds.filter((id) => !isPaused(id));
      if (targets.length === 0) return;

      setLoadingIds((prev) => {
        const next = new Set(prev);
        for (const id of targets) next.add(id);
        return next;
      });

      try {
        if (force) {
          for (const id of targets) {
            clearFoldersSessionCache(id);
          }
        }

        for (const accountId of targets) {
          await syncFolderHeaders(accountId, "INBOX");
        }

        const results = await fetchMailFoldersSequential(
          targets.map((accountId) => ({ accountId, refresh: force }))
        );

        setFoldersByAccount((prev) => {
          const next = new Map(prev);
          targets.forEach((accountId, index) => {
            const res = results[index];
            if (!res.ok) return;
            const list = normalizeMailFolderLabels(
              res.data.folders?.length ? res.data.folders : FALLBACK_FOLDERS
            );
            writeFoldersSessionCache(accountId, list);
            ensureAureliaFolderOverrides(accountId, list);
            enqueueStandardFolderWarm(accountId, list);
            if (accountId !== activeAccountId) {
              next.set(accountId, list);
            }
          });
          return next;
        });
      } finally {
        setLoadingIds((prev) => {
          const next = new Set(prev);
          for (const id of targets) next.delete(id);
          return next;
        });
      }
    },
    [activeAccountId, isPaused]
  );

  return { getFolders, isFoldersLoading, refreshAllAccounts, adjustAccountFolderUnread };
}
